import { useState, useRef } from "react";
import PageSEO from "@/components/PageSEO";
import { useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  MapPin, Zap, CheckCircle2, Circle, AlertCircle, ChevronDown, ChevronUp,
  ArrowRight, Car, Building2, Sun, Wifi,
} from "lucide-react";

const F = {
  950: "#2d2219", 900: "#3a2e28", 800: "#504136", 700: "#5e4e41", 600: "#6e5c4e",
  500: "#689689", 400: "#83E8BA", 300: "#8ab0a8",
  200: "#B2E6D4", 100: "#c8ede4", 50: "#edf8f4",
  e500: "#A49E8D", e400: "#8c8678", e300: "#c0b9ac", e100: "#f0ede8",
  m600: "#2d4f4b", m500: "#3d6b65", m400: "#4a7068", m300: "#5e8480",
  c100: "#f5f2ec", c200: "#e8e3d8", c50: "#faf9f6",
};

function Reveal({ children, className, delay = 0, y = 25 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const CRITERIA = [
  {
    category: "Land & Access",
    icon: MapPin,
    color: F[500],
    items: [
      { label: "Minimum 200 sq m available for a viable 2-charger station", weight: "critical" },
      { label: "Clear vehicle entry and exit with turning radius ≥ 7.5 m", weight: "critical" },
      { label: "Level or gently sloping ground (< 5° grade)", weight: "important" },
      { label: "Proximity to NH/SH or high-traffic commercial corridor", weight: "important" },
      { label: "No major encumbrances, flood risk, or soil instability", weight: "important" },
    ],
  },
  {
    category: "Grid & Power",
    icon: Zap,
    color: F.e400,
    items: [
      { label: "HT or LT power line within 100 m (for DC fast chargers)", weight: "critical" },
      { label: "DISCOM willing to provide ≥ 100 kVA sanctioned load", weight: "critical" },
      { label: "Three-phase supply available or feasible within budget", weight: "critical" },
      { label: "Power outage frequency < 4 hrs/day (check CEA reliability maps)", weight: "important" },
      { label: "Net metering policy active in the state for rooftop solar", weight: "nice" },
    ],
  },
  {
    category: "Traffic & Demand",
    icon: Car,
    color: F[400],
    items: [
      { label: "Daily vehicular traffic > 5,000 vehicles (for DC hubs)", weight: "critical" },
      { label: "EV penetration > 3% in the district (check VAHAN data)", weight: "important" },
      { label: "Nearby anchors: hotel, mall, hospital, restaurant, or highway rest stop", weight: "important" },
      { label: "Average dwell time ≥ 20 min (DC fast) or ≥ 60 min (AC)", weight: "important" },
      { label: "No competing EV hub within 2 km (highway) or 500 m (urban)", weight: "nice" },
    ],
  },
  {
    category: "Permits & Compliance",
    icon: Building2,
    color: F.m400,
    items: [
      { label: "Land use zoning permits commercial activity (check local DP/master plan)", weight: "critical" },
      { label: "No NGT or CRZ (coastal) restrictions on the parcel", weight: "critical" },
      { label: "State EV policy includes this location in priority zones", weight: "important" },
      { label: "CEA approval pathway confirmed with local DISCOM", weight: "important" },
      { label: "Fire NOC feasible under NBC 2016 norms for the proposed layout", weight: "important" },
    ],
  },
  {
    category: "Solar Potential",
    icon: Sun,
    color: F.e500,
    items: [
      { label: "GHI (global horizontal irradiance) > 4.5 kWh/m²/day", weight: "important" },
      { label: "Rooftop or canopy area available for ≥ 20 kWp PV system", weight: "nice" },
      { label: "State discom has open-access or net metering > 10 kW", weight: "nice" },
      { label: "No shading from tall structures between 9 AM – 4 PM", weight: "nice" },
    ],
  },
  {
    category: "Connectivity",
    icon: Wifi,
    color: F[300],
    items: [
      { label: "4G/5G signal strength adequate for payment terminal & OCPP", weight: "critical" },
      { label: "Fibre or broadband available as backup for billing system", weight: "important" },
      { label: "Site visible or discoverable on Google Maps / major EV apps", weight: "nice" },
    ],
  },
];

const WEIGHT_CONFIG = {
  critical:  { label: "Critical",  color: "#ef4444", bg: "#fef2f2" },
  important: { label: "Important", color: F[800],    bg: F[50] },
  nice:      { label: "Nice to have", color: F.m400,  bg: "#f8f8f8" },
};

const LOCATION_TYPES = [
  {
    type: "Urban — Shopping & Commercial",
    bestFor: ["AC 22 kW workplace chargers", "Slow DC 30 kW top-up hubs"],
    minArea: "150 sq m",
    avgSessions: "8–15/day/charger",
    keyRisk: "High land cost, parking restrictions",
  },
  {
    type: "Highway — National & State Roads",
    bestFor: ["DC 60–120 kW fast hubs", "HPC ≥ 150 kW superhubs"],
    minArea: "500 sq m",
    avgSessions: "10–20/day/charger",
    keyRisk: "Grid availability, storm water drainage",
  },
  {
    type: "Semi-Urban — Tier 2 & 3 Towns",
    bestFor: ["DC 30–60 kW mixed use", "AC fleet charging overnight"],
    minArea: "300 sq m",
    avgSessions: "4–10/day/charger",
    keyRisk: "Lower EV penetration today, high upside",
  },
  {
    type: "Industrial — SEZ & Logistics Parks",
    bestFor: ["AC 22 kW fleet charging", "DC 60 kW captive fleet"],
    minArea: "200 sq m",
    avgSessions: "Shift-based (predictable)",
    keyRisk: "Captive load, limited public revenue",
  },
];

function CriteriaSection({ category, icon: Icon, color, items }) {
  const [open, setOpen] = useState(true);
  const [checked, setChecked] = useState({});

  const toggle = idx => setChecked(c => ({ ...c, [idx]: !c[idx] }));
  const doneCount = Object.values(checked).filter(Boolean).length;

  return (
    <div
      className="rounded-2xl border overflow-hidden"
      style={{ backgroundColor: "#fff", borderColor: F[200] }}
    >
      <button
        className="w-full flex items-center justify-between px-5 py-4"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: color + "22" }}
          >
            <Icon size={16} style={{ color }} />
          </div>
          <span className="font-bold text-sm" style={{ color: F[800], fontFamily: "Chivo, sans-serif" }}>
            {category}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs" style={{ color: F.m400 }}>
            {doneCount}/{items.length}
          </span>
          {open
            ? <ChevronUp size={16} style={{ color: F.m400 }} />
            : <ChevronDown size={16} style={{ color: F.m400 }} />
          }
        </div>
      </button>

      {open && (
        <ul className="border-t divide-y" style={{ borderColor: F[200] }}>
          {items.map((item, idx) => {
            const wc = WEIGHT_CONFIG[item.weight];
            return (
              <li
                key={idx}
                className="flex items-start gap-3 px-5 py-3 cursor-pointer transition-colors"
                style={{ backgroundColor: checked[idx] ? F[50] : "#fff" }}
                onClick={() => toggle(idx)}
              >
                {checked[idx]
                  ? <CheckCircle2 size={17} style={{ color: F[500], flexShrink: 0, marginTop: 1 }} />
                  : <Circle size={17} style={{ color: F[200], flexShrink: 0, marginTop: 1 }} />
                }
                <div className="flex-1 min-w-0">
                  <p
                    className="text-sm leading-snug"
                    style={{ color: checked[idx] ? F.m400 : F[800], textDecoration: checked[idx] ? "line-through" : "none" }}
                  >
                    {item.label}
                  </p>
                </div>
                <span
                  className="text-xs px-2 py-0.5 rounded-full shrink-0"
                  style={{ backgroundColor: wc.bg, color: wc.color, fontWeight: 500 }}
                >
                  {wc.label}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function SiteSelection() {
  const navigate = useNavigate();

  const totalItems = CRITERIA.reduce((s, c) => s + c.items.length, 0);

  return (
    <div className="min-h-screen" style={{ backgroundColor: F.c100, fontFamily: "Manrope, sans-serif" }}>
      <PageSEO
        title="EV Charging Station Site Selection Tool for India"
        description="Find the best location for your EV charging station in India. Analyse traffic, competition, land cost and proximity to highways or commercial zones with AI-powered site scoring."
        path="/site-selection"
      />
      {/* Header */}
      <header
        className="sticky top-0 z-40 backdrop-blur-md"
        style={{ backgroundColor: F[800] + "f2", borderBottom: `1px solid ${F[700]}` }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <button
            onClick={() => navigate("/")}
            style={{ color: F[200], fontFamily: "Chivo, sans-serif", fontWeight: 700, fontSize: "1rem" }}
          >
            EV Hub Studio
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/planner")}
              className="text-sm hidden sm:block"
              style={{ color: F[300] }}
            >
              Planner
            </button>
            <button
              onClick={() => navigate("/try")}
              className="text-sm px-4 py-1.5 rounded-lg font-semibold"
              style={{ backgroundColor: F[500], color: "#fff" }}
            >
              Try Free
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8 text-center">
        <Reveal>
          <span
            className="inline-block text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-4"
            style={{ backgroundColor: F[500] + "20", color: F[500] }}
          >
            Site Selection Guide
          </span>
          <h1
            className="text-3xl sm:text-4xl font-bold mb-4"
            style={{ color: F[800], fontFamily: "Chivo, sans-serif" }}
          >
            How to choose the right site for<br />
            <span style={{ color: F[500] }}>an EV charging station in India</span>
          </h1>
          <p className="text-base leading-relaxed max-w-2xl mx-auto" style={{ color: F.m400 }}>
            Use this interactive checklist to evaluate any site across {totalItems} criteria —
            land, grid, traffic, permits, solar potential, and connectivity. Tick off as you go.
          </p>
        </Reveal>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid lg:grid-cols-3 gap-6">

          {/* Checklist */}
          <div className="lg:col-span-2 space-y-4">
            <Reveal>
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle size={15} style={{ color: F.e500 }} />
                <p className="text-xs" style={{ color: F.m400 }}>
                  Click any item to mark it complete. Progress is not saved — use the full planner to record site evaluations.
                </p>
              </div>
            </Reveal>
            {CRITERIA.map((c, i) => (
              <Reveal key={c.category} delay={i * 0.05}>
                <CriteriaSection {...c} />
              </Reveal>
            ))}
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            {/* Location type guide */}
            <Reveal delay={0.1}>
              <div
                className="rounded-2xl border p-5"
                style={{ backgroundColor: "#fff", borderColor: F[200] }}
              >
                <h3
                  className="font-bold text-sm mb-4"
                  style={{ color: F[800], fontFamily: "Chivo, sans-serif" }}
                >
                  Site type guide
                </h3>
                <div className="space-y-4">
                  {LOCATION_TYPES.map(lt => (
                    <div
                      key={lt.type}
                      className="rounded-xl p-3"
                      style={{ backgroundColor: F.c100 }}
                    >
                      <p className="font-semibold text-xs mb-2" style={{ color: F[800] }}>{lt.type}</p>
                      <div className="space-y-1 text-xs" style={{ color: F.m500 }}>
                        <p><span style={{ color: F[800], fontWeight: 600 }}>Best for:</span> {lt.bestFor.join(", ")}</p>
                        <p><span style={{ color: F[800], fontWeight: 600 }}>Min area:</span> {lt.minArea}</p>
                        <p><span style={{ color: F[800], fontWeight: 600 }}>Sessions:</span> {lt.avgSessions}</p>
                        <p><span style={{ color: "#ef4444", fontWeight: 600 }}>Watch out:</span> {lt.keyRisk}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            {/* AI analysis CTA */}
            <Reveal delay={0.15}>
              <div
                className="rounded-2xl p-5 text-center"
                style={{ backgroundColor: F[900] }}
              >
                <MapPin size={24} className="mx-auto mb-3" style={{ color: F[400] }} />
                <h3
                  className="font-bold text-sm mb-2"
                  style={{ color: F[200], fontFamily: "Chivo, sans-serif" }}
                >
                  Let AI evaluate your site
                </h3>
                <p className="text-xs mb-4 leading-relaxed" style={{ color: F[300] }}>
                  Enter your location and land details. The planner auto-classifies the site,
                  checks state tariffs, flags constraints, and recommends the right charger mix.
                </p>
                <button
                  onClick={() => navigate("/try")}
                  className="flex items-center gap-2 mx-auto px-5 py-2 rounded-xl text-sm font-semibold hover:opacity-90"
                  style={{ backgroundColor: F[500], color: "#fff", fontFamily: "Chivo, sans-serif" }}
                >
                  Analyse My Site <ArrowRight size={15} />
                </button>
              </div>
            </Reveal>

            {/* Key weight legend */}
            <Reveal delay={0.2}>
              <div
                className="rounded-2xl border p-4"
                style={{ backgroundColor: "#fff", borderColor: F[200] }}
              >
                <p className="text-xs font-semibold mb-3" style={{ color: F[800] }}>Criteria weights</p>
                {Object.entries(WEIGHT_CONFIG).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 mb-1.5">
                    <span
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: v.bg, color: v.color, fontWeight: 500 }}
                    >
                      {v.label}
                    </span>
                    <span className="text-xs" style={{ color: F.m400 }}>
                      {k === "critical" && "Deal-breaker if absent"}
                      {k === "important" && "Strongly affects viability"}
                      {k === "nice" && "Improves returns"}
                    </span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}
